import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { toast } from 'sonner';
import { useReferenceSelection } from '../../../shared/contexts/ReferenceSelectionContext';

export function SelectionActionBar() {
  const { selectedReferences, clearSelection, selectionCount } = useReferenceSelection();
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the target project from URL if present
  const targetProjectId = searchParams.get('addToProject') as Id<'projects'> | null;

  // Fetch user's active projects
  const projects = useQuery(api.projects.getUserProjects, { status: 'active' }) || [];

  // Fetch target project details if specified
  const targetProject = useQuery(
    api.projects.getProject,
    targetProjectId ? { projectId: targetProjectId } : 'skip'
  );

  // Mutation to add references to project
  const addReferenceToProject = useMutation(api.projects.addReferenceToProject);

  if (selectionCount === 0) {
    return null;
  }

  const handleAddToProject = async (projectId: Id<'projects'>, navigateBack: boolean = false) => {
    setIsAdding(true);
    try {
      const referenceIds = Array.from(selectedReferences);
      let successCount = 0;
      let alreadyExistCount = 0;

      for (const referenceId of referenceIds) {
        try {
          await addReferenceToProject({
            projectId,
            referenceId,
          });
          successCount++;
        } catch (error) {
          if (error instanceof Error && error.message.includes('already in the project')) {
            alreadyExistCount++;
          } else {
            console.error(`Failed to add reference ${referenceId}:`, error);
          }
        }
      }

      if (successCount > 0) {
        toast.success(`Added ${successCount} reference${successCount === 1 ? '' : 's'} to project!`);
      }
      if (alreadyExistCount > 0) {
        toast.info(`${alreadyExistCount} reference${alreadyExistCount === 1 ? ' was' : 's were'} already in the project`);
      }

      clearSelection();
      setShowProjectSelector(false);

      // Navigate back to project if requested
      if (navigateBack) {
        setTimeout(() => {
          navigate(`/projects/${projectId}`);
        }, 500);
      }
    } catch (error) {
      console.error('Error adding references to project:', error);
      toast.error('Failed to add references to project');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      {/* Fixed Bottom Action Bar */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-gray-900 text-white rounded-full shadow-2xl px-6 py-4 flex items-center gap-4 border-2 border-yellow-400">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-bold">{selectionCount} selected</span>
          </div>

          <div className="h-6 w-px bg-gray-600" />

          {/* Show quick-add button if target project is specified */}
          {targetProject && (
            <>
              <button
                onClick={() => handleAddToProject(targetProjectId!, true)}
                disabled={isAdding}
                className="btn btn-sm bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add to "{targetProject.name}"
              </button>
              <button
                onClick={() => setShowProjectSelector(true)}
                disabled={isAdding}
                className="btn btn-sm btn-outline border-white text-white hover:bg-white hover:text-gray-900"
                title="Choose a different project"
              >
                Other Project
              </button>
            </>
          )}

          {/* Show regular button if no target project */}
          {!targetProject && (
            <button
              onClick={() => setShowProjectSelector(true)}
              className="btn btn-sm bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add to Project
            </button>
          )}

          <button
            onClick={clearSelection}
            className="btn btn-sm btn-ghost text-white hover:bg-gray-800"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Project Selector Modal */}
      {showProjectSelector && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl bg-base-100 border-2 border-base-300">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-base-content">
                Add {selectionCount} reference{selectionCount === 1 ? '' : 's'} to project
              </h3>
              <button
                type="button"
                onClick={() => setShowProjectSelector(false)}
                className="btn btn-sm btn-circle btn-ghost"
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {projects.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No active projects</h4>
                <p className="text-sm text-gray-600 mb-4">Create a project first to organize your references</p>
                <button
                  onClick={() => {
                    setShowProjectSelector(false);
                    window.location.assign('/projects');
                  }}
                  className="btn bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
                >
                  Create Project
                </button>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {projects.map((project) => (
                  <button
                    key={project._id}
                    onClick={() => handleAddToProject(project._id)}
                    disabled={isAdding}
                    className="w-full p-4 rounded-lg border-2 border-base-300 hover:border-yellow-400 hover:bg-yellow-50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-base-content">{project.name}</h4>
                        {project.description && (
                          <p className="text-sm text-base-content/70 mt-1 line-clamp-2">{project.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-base-content/60">
                          <span>{project.referenceCount} references</span>
                          <span className="capitalize">{project.phase}</span>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="modal-action">
              <button
                type="button"
                onClick={() => setShowProjectSelector(false)}
                disabled={isAdding}
                className="btn btn-ghost"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
